import React from "react";
import ReactGA from "react-ga";
import { Button, Form } from "react-bootstrap";
import styled from "styled-components";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import RangeSlider from "react-bootstrap-range-slider";
import "react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth0 } from "@auth0/auth0-react";
import { shallowEqual } from "react-redux";

import "./App.scss";
import Footer from "./components/layout/Footer";
import Header from "./components/layout/Header";
import {
  useApiVehiclesToBlock,
  appName,
  contentOnlyForTA,
  fetchInterval,
  initialTableWidth,
  disableButtons,
  configAgencyName,
  downloadableStopsData,
  authorizationHeaderApiGTFSv2,
} from "./config";
import NotFound from "./components/layout/NotFound";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import {
  fetchActiveAssignmentsToBlocks,
  getActiveBlocks,
  getBlocksIds,
  getBuses,
  getCredentials,
  getDownloadableTA_content,
  getRoutesDetails,
  getRoutesList,
  getScheduleDetails,
  getCurrentServiceId,
  setLoadingTable,
  getVehicleSiriData,
  dispatchBlockStatistics,
  getCurrentCalendarBlockIds,
  getGTFS_RT_tripUpdates,
  getBusesIds,
} from "./redux/actions";
import {
  addCancelReenableData,
  addColorsToBuses,
  addSiriData,
  addTripPattern,
  busesManipulation,
  dateToString2,
  dynamicTextColor,
  getAcronym,
  valueFromLocalStorage,
} from "./utils/helpers";
import TailSpinner from "./components/layout/TailSpinner";
import ButtonsLegend from "./components/layout/ButtonsLegend";
import MapComponent from "./components/MapComponent";
import UseShifts from "./components/services/UseShifts";
import variableColors from "./_App.module.scss";
import sidebarHideIcon from "./assets/icons/sidebarHideIcon.svg";
import sidebarShowIcon from "./assets/icons/sidebarShowIcon.svg";
import appLazyImports from "./appLazyImports";
import ProtectedComponent from "./components/helpers/ProtectedComponent"; //* ProtectedComponent
import useAdditionalData from "./hooks/useAdditionalData";
import { AgencyContext } from "./hooks/AgencyContext";
import { exportTypeAVL, exportTypeDefault } from "./data/constants";

//* Lazy imports
const {
  TableBigComponent,
  SingleTripReport,
  DeviationReport,
  ActiveBlocksAccordion,
  SingleReportAVL,
  ScheduleForRoute,
  Last24HoursReport,
  AllTripsReport,
  AllVehiclesAVLReport,
  DownloadAllVehiclesAVL,
  CompareApisData,
  BrigadeScheduleReport,
  SelectedBusView,
  PredictionAccuracyInterval,
  PredictionAccuracyScatter,
  PredictionAccuracyRange,
  ServiceAlertsManager,
  AddNewAlert,
  AlertDetails,
  StopPunctualityReport,
  RoutesInfo,
  ModifyTripStatus,
  BusLocationByDate,
  AllStopsPunctualityReportDownLoad,
  ScheduleAdherenceAllRoutes,
  AverageSpeeds,
  DownloadDailyStopsAdherence,
  HeadwayPerformanceTable,
  SynopticLiveLadderView,
  VehicleToBlockReport,
  TableBigComponentV2Wrapper,
  DeleteVehiclesCache,
} = appLazyImports;

//* _App.module.scss
const { fullHDresolution, darkColor } = variableColors;

const DivSwitch = styled.div`
  width: 100%;
  height: 31px;
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  justify-content: flex-start;
  align-items: center;
  align-content: center;
  @media screen and (max-width: ${fullHDresolution}) {
    gap: 1rem;
  }
`;

const DivRange = styled.div`
  width: 220px;
  height: 31px;
  display: flex;
  flex-direction: row;
  gap: 0.8rem;
  justify-content: flex-start;
  align-items: center;
  align-content: center;
  span {
    font-size: 100%;
    color: ${darkColor};
  }
  @media screen and (max-width: ${fullHDresolution}) {
    width: 150px;
    span {
      font-size: 90%;
    }
  }
`;

const TRACKING_ID = process.env.REACT_APP_googleTrackingId as string;
ReactGA.initialize(TRACKING_ID);

function App(): JSX.Element {
  //* Google Analytics
  React.useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, []);

  //* Setting App name
  React.useEffect(() => {
    document.title = appName;
  }, []);

  const { t } = useTranslation();
  const location = useLocation();
  const dispatch: Dispatch = useAppDispatch();
  const { loginWithRedirect, isAuthenticated, user, logout, isLoading } = useAuth0();

  //* AgencyContext
  const { initialMapRange, agencyId } = React.useContext(AgencyContext) as AgencyContextType;

  //* Custom Hook
  const getAdditionalData: () => void = useAdditionalData();

  const [
    busesFromRedux,
    activeBlocksFromRedux,
    vehicleSiriData,
    loadingTable,
    routesDetails,
    bearerToken,
    mapSettingsFromRedux,
    currentCalendar,
    scheduleRelationshipTripsArray,
    allTripsArray,
    busesIds,
    stopFetching,
  ]: [
    {
      busesOnTime: BusInfo[];
      busesTooFast: BusInfo[];
      delayedBuses: BusInfo[];
      remainingBuses: BusInfo[];
      busesWithLayover: BusInfo[];
    },
    ActiveBlock[],
    VehicleSiriData[],
    boolean,
    RouteDetails[],
    string,
    MapSettings,
    CurrentCalendar[],
    CancelReenableTrip[],
    TripPattern[],
    string[],
    boolean,
  ] = useAppSelector(
    (state: RootState) => [
      state?.buses,
      state?.activeBlocks?.routes,
      state?.activeBlocks?.vehicleSiriData,
      state?.constantValues?.loadingTable,
      state?.routesInfo?.routesInfo,
      state?.constantValues?.credentials?.access_token,
      state?.constantValues?.mapSettings,
      state?.constantValues?.currentCalendar,
      state?.buses?.scheduleRelationshipTripsArray,
      state?.routesInfo?.allTripsArray,
      state?.constantValues?.busesIds,
      state?.constantValues?.stopFetching,
    ],
    shallowEqual
  );
  // console.log("stopFetching:", stopFetching);

  const [buses, setBuses] = React.useState<{
    busesOnTime: BusInfo[];
    busesTooFast: BusInfo[];
    delayedBuses: BusInfo[];
    remainingBuses: BusInfo[];
    busesWithLayover: BusInfo[];
  } | null>(null);
  const [activeBlocks, setActiveBlock] = React.useState<Vehicle[]>([]);
  const [showFreeBuses, setShowFreeBuses] = React.useState<boolean>(
    valueFromLocalStorage("showFreeBuses") ? JSON.parse(valueFromLocalStorage("showFreeBuses") as string) : false
  );
  const [selectedMapUrl, setSelectedMapUrl] = React.useState<string>(
    valueFromLocalStorage("selectedMapUrl")
      ? JSON.parse(valueFromLocalStorage("selectedMapUrl") as string)
      : configAgencyName === ("Swarzedz" as string)
        ? "mapbox"
        : "openstreetmap"
  );
  const [tableWidth, setTableWidth] = React.useState<number>(initialTableWidth);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [hideMap, setHideMap] = React.useState<boolean>(false);
  const [hideSidebar, setHideSidebar] = React.useState<boolean>(false);

  const hideShowTableHandler = async () => {
    await dispatch(setLoadingTable(true));
    await setLoading(true);
    setHideSidebar(!hideSidebar);
  };

  //^ Get additional data
  React.useEffect(() => {
    if (!isAuthenticated) return;
    if (isAuthenticated && authorizationHeaderApiGTFSv2) {
      getAdditionalData();
    }
  }, [getAdditionalData, isAuthenticated]);

  //* Show or Hide Map on the right side
  React.useEffect(() => {
    if (location?.pathname.includes("/window") || location?.pathname.includes("/tableV2")) {
      setHideMap(true);
    } else {
      setHideMap(false);
    }
  }, [location?.pathname]);

  //* Save settings in LocalStorage
  //* tableWidth -> set in Header!
  React.useEffect(() => {
    const localStorageSettings = {
      showFreeBuses,
      selectedMapUrl,
    };
    Object.entries(localStorageSettings).forEach((entry) => localStorage.setItem(entry[0], JSON.stringify(entry[1])));
  }, [selectedMapUrl, showFreeBuses]);

  // setLoading -> Loader - Spinner -> Map
  React.useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, [loading]);

  //^ When colorMap is changed: setLoading -> Loader - Spinner -> Map
  React.useEffect(() => {
    setLoading(true);
  }, [mapSettingsFromRedux?.colorMap]);

  // setLoadingTable -> Loader - Spinner -> Table
  // Initial setup
  React.useEffect(() => {
    dispatch(setLoadingTable(false));
  }, [dispatch]);

  // setLoadingTable -> Loader - Spinner -> Table
  // After vehicle assignment
  React.useEffect(() => {
    setTimeout(() => {
      dispatch(setLoadingTable(false));
    }, 500);
  }, [dispatch, loadingTable]);

  //* Initial Fetching: get Routes Details + Blocks-Ids + Line Numbers + BusesIds
  React.useEffect(() => {
    if (!isAuthenticated || !agencyId) return;
    if (isAuthenticated) {
      (async function (): Promise<void> {
        await dispatch(getRoutesDetails());
        await dispatch(getBlocksIds());
        await dispatch(getRoutesList());
        await dispatch(getBusesIds());
      })();
    }
  }, [agencyId, dispatch, isAuthenticated]);

  // Initial Fetch Active Blocks
  React.useEffect(() => {
    if (!isAuthenticated || !agencyId || stopFetching) return;
    const setFetchInterval = () => {
      dispatch(getActiveBlocks());
      // console.log(1, "ActiveBlock were fetched", new Date().toLocaleTimeString("pl-PL"));
    };
    setFetchInterval();
    const fetchingInterval = setInterval(setFetchInterval, fetchInterval) as NodeJS.Timer;
    return () => {
      clearInterval(fetchingInterval);
      // console.log("Interval Active Blocks was cleared");
    };
  }, [agencyId, dispatch, isAuthenticated, stopFetching]);

  // Initial Fetch To Get Bus Groups
  React.useEffect(() => {
    if (!isAuthenticated || !agencyId || stopFetching) return;
    if (busesIds && busesIds?.length) {
      // console.log("busesIds:", busesIds);
      const setFetchInterval = async () => {
        await dispatch(getBuses());
        await dispatch(dispatchBlockStatistics(new Date()));
        await dispatch(getGTFS_RT_tripUpdates());
        // console.log(2, "Bus Groups were fetched", new Date().toLocaleTimeString("pl-PL"));
      };
      setFetchInterval();
      const fetchingInterval = setInterval(setFetchInterval, fetchInterval) as NodeJS.Timer;
      return () => {
        clearInterval(fetchingInterval);
        // console.log("Interval Bus Groups was cleared");
      };
    }
  }, [agencyId, busesIds, dispatch, isAuthenticated, stopFetching]);

  //* Initial Fetch -> Get Vehicle Siri Data
  React.useEffect(() => {
    if (!isAuthenticated || !agencyId || stopFetching) return;
    if (activeBlocksFromRedux) {
      const activeBlocksArray = activeBlocksFromRedux?.map((block) => block?.id) as string[];
      // console.log("activeBlocksArray:", activeBlocksArray);
      const activeBlocksArrayDouble = activeBlocksArray.map((block) => ["r", block]);
      // console.log("activeBlocksArrayDouble:", activeBlocksArrayDouble);
      const searchParams = new URLSearchParams(activeBlocksArrayDouble) as URLSearchParams;
      // console.log("searchParams:", searchParams);
      const paramsSize = [...new Set(searchParams.keys())].length;
      // console.log({ paramsSize });

      const setFetchInterval = () => {
        paramsSize && searchParams && dispatch(getVehicleSiriData(searchParams));
        // console.log(3, "SiriData were fetched", new Date().toLocaleTimeString());
      };
      setFetchInterval();
      const fetchingInterval = setInterval(setFetchInterval, fetchInterval) as NodeJS.Timer;
      return () => {
        clearInterval(fetchingInterval);
        // console.log("Interval SiriData was cleared");
      };
    }
  }, [activeBlocksFromRedux, agencyId, dispatch, isAuthenticated, stopFetching]);

  //* Valid Assignments To Blocks (vehiclesToBlock)
  React.useEffect(() => {
    if (!isAuthenticated || !useApiVehiclesToBlock || !agencyId || stopFetching) return;
    if (useApiVehiclesToBlock) {
      const setFetchInterval = () => {
        dispatch(fetchActiveAssignmentsToBlocks());
        // console.log(4, "Assignments were fetched", new Date().toLocaleTimeString());
      };
      setFetchInterval();
      const fetchingInterval = setInterval(setFetchInterval, fetchInterval) as NodeJS.Timer;
      return () => {
        clearInterval(fetchingInterval);
        // console.log("Interval Assignments to Blocks was cleared");
      };
    }
  }, [agencyId, dispatch, isAuthenticated, stopFetching]);

  // Data Manipulation
  React.useEffect(() => {
    if (!isAuthenticated || !agencyId) return;
    if (activeBlocksFromRedux) {
      try {
        const vehiclesArray = [] as Vehicle[];
        for (let i = 0; i < activeBlocksFromRedux.length; i++) {
          for (let j = 0; j < activeBlocksFromRedux[i].block.length; j++) {
            if (activeBlocksFromRedux[i].block[j].vehicle.length >= 1) {
              for (let k = 0; k < activeBlocksFromRedux[i].block[j].vehicle.length; k++) {
                // console.log(activeBlocksFromRedux[i].block[j].vehicle[k]);
                const blockInfo = {
                  startTime: activeBlocksFromRedux[i].block[j].startTime,
                  endTime: activeBlocksFromRedux[i].block[j].endTime,
                  trip: activeBlocksFromRedux[i].block[j].trip,
                  serviceId: activeBlocksFromRedux[i].block[j].serviceId,
                };
                activeBlocksFromRedux[i].block[j].vehicle[k].blockInfo = blockInfo;
                vehiclesArray.push(activeBlocksFromRedux[i].block[j].vehicle[k]);
              }
            }
          }
        }
        // console.log("vehiclesArray:", vehiclesArray);
        setActiveBlock(vehiclesArray);
      } catch (error) {
        console.log("error:", (error as CustomError).message);
      }
    }
  }, [activeBlocksFromRedux, agencyId, isAuthenticated]);

  // Data Manipulation
  React.useEffect(() => {
    if (!isAuthenticated || !agencyId) return;
    if (
      busesIds &&
      busesFromRedux &&
      Object.keys(busesFromRedux).length > 0 &&
      activeBlocks &&
      routesDetails &&
      allTripsArray
    ) {
      try {
        let { remainingBuses } = busesFromRedux || {};
        let busesOnTime = busesFromRedux?.busesOnTime || [];
        let busesTooFast = busesFromRedux?.busesTooFast || [];
        let delayedBuses = busesFromRedux?.delayedBuses || [];
        let busesWithLayover = busesFromRedux?.busesWithLayover || [];

        const remainingBusesEmpty: BusInfo[] = [];
        let remainingBusesToPass: BusInfo[] = showFreeBuses ? remainingBuses : remainingBusesEmpty;

        //* Adding additional data from other API endpoint
        busesOnTime = busesManipulation(busesOnTime, activeBlocks);
        busesTooFast = busesManipulation(busesTooFast, activeBlocks);
        delayedBuses = busesManipulation(delayedBuses, activeBlocks);

        //* Adding additional data from other API endpoint - Route Details
        busesOnTime = addColorsToBuses(busesOnTime, routesDetails);
        busesTooFast = addColorsToBuses(busesTooFast, routesDetails);
        delayedBuses = addColorsToBuses(delayedBuses, routesDetails);

        //* Adding cancel/reenable trip data
        if (scheduleRelationshipTripsArray && scheduleRelationshipTripsArray?.length) {
          busesOnTime = addCancelReenableData(busesOnTime, scheduleRelationshipTripsArray);
          busesTooFast = addCancelReenableData(busesTooFast, scheduleRelationshipTripsArray);
          delayedBuses = addCancelReenableData(delayedBuses, scheduleRelationshipTripsArray);
        }

        //* Adding tripPattern data
        busesOnTime = addTripPattern(busesOnTime, allTripsArray);
        busesTooFast = addTripPattern(busesTooFast, allTripsArray);
        delayedBuses = addTripPattern(delayedBuses, allTripsArray);

        //* Changing dynamically textColor depended on backgroundColor
        for (let i = 0; i < busesOnTime?.length; i++) {
          busesOnTime[i].routeInfoAdded.textColor = dynamicTextColor(busesOnTime[i]?.routeInfoAdded?.color);
        }
        for (let i = 0; i < busesTooFast?.length; i++) {
          busesTooFast[i].routeInfoAdded.textColor = dynamicTextColor(busesTooFast[i]?.routeInfoAdded?.color);
        }
        for (let i = 0; i < delayedBuses?.length; i++) {
          delayedBuses[i].routeInfoAdded.textColor = dynamicTextColor(delayedBuses[i]?.routeInfoAdded?.color);
        }

        //* Add Acronym if agency is Miami Beach
        if (configAgencyName === ("MiamiBeach" as string)) {
          for (let i = 0; i < busesOnTime.length; i++) {
            busesOnTime[i].acronym = getAcronym(busesOnTime[i]?.routeShortName);
          }
          for (let i = 0; i < busesTooFast.length; i++) {
            busesTooFast[i].acronym = getAcronym(busesTooFast[i]?.routeShortName);
          }
          for (let i = 0; i < delayedBuses.length; i++) {
            delayedBuses[i].acronym = getAcronym(delayedBuses[i]?.routeShortName);
          }
        }

        //* Adding Siri info: monitored: true or false
        if (vehicleSiriData) {
          busesOnTime = addSiriData(busesOnTime, vehicleSiriData);
          busesTooFast = addSiriData(busesTooFast, vehicleSiriData);
          delayedBuses = addSiriData(delayedBuses, vehicleSiriData);
        }

        //* Sorting remainingBuses
        remainingBusesToPass = remainingBusesToPass.sort(function (a, b) {
          return a.id > b.id ? 1 : b.id > a.id ? -1 : 0;
        });

        //^ Buses with Layover
        if (busesWithLayover?.length) {
          busesWithLayover = busesManipulation(busesWithLayover, activeBlocks);
          busesWithLayover = addColorsToBuses(busesWithLayover, routesDetails);
          busesWithLayover = addCancelReenableData(busesWithLayover, scheduleRelationshipTripsArray);
          busesWithLayover = addTripPattern(busesWithLayover, allTripsArray);

          for (let i = 0; i < busesWithLayover.length; i++) {
            if (busesWithLayover[i]?.routeInfoAdded?.textColor) {
              busesWithLayover[i].routeInfoAdded.textColor = dynamicTextColor(busesWithLayover[i]?.routeInfoAdded?.color);
            }
          }

          //* Add Acronym if agency is Miami Beach
          if (configAgencyName === ("MiamiBeach" as string)) {
            for (let i = 0; i < busesWithLayover.length; i++) {
              busesWithLayover[i].acronym = getAcronym(busesWithLayover[i]?.routeShortName);
            }
          }

          //* Adding Siri info: monitored: true or false
          if (vehicleSiriData) {
            busesWithLayover = addSiriData(busesWithLayover, vehicleSiriData);
          }
          // console.log("busesWithLayover:", busesWithLayover);
        }

        const busesToSet = {
          busesOnTime,
          busesTooFast,
          delayedBuses,
          remainingBuses: remainingBusesToPass,
          busesWithLayover,
        };
        setBuses(busesToSet);
      } catch (error) {
        console.log("error:", (error as CustomError)?.message);
      }
    }
  }, [
    activeBlocks,
    agencyId,
    allTripsArray,
    busesFromRedux,
    busesIds,
    isAuthenticated,
    routesDetails,
    scheduleRelationshipTripsArray,
    showFreeBuses,
    vehicleSiriData,
  ]);

  //* Get current serviceId
  React.useEffect(() => {
    if (!isAuthenticated || !agencyId || stopFetching) return;
    const setFetchInterval = () => {
      dispatch(getCurrentServiceId());
      // console.log(5, "serviceId was fetched", new Date().toLocaleTimeString());
    };
    setFetchInterval();
    const fetchingInterval = setInterval(setFetchInterval, fetchInterval) as NodeJS.Timer;
    return () => {
      clearInterval(fetchingInterval);
      // console.log("Interval get serviceId was cleared");
    };
  }, [agencyId, dispatch, isAuthenticated, stopFetching]);

  //^ Get current blockIds for current serviceIds
  React.useEffect(() => {
    if (!isAuthenticated || !agencyId) return;
    //* Needed if ActionModalAssign is enabled!
    if (currentCalendar && !disableButtons) {
      const currentServiceIds = currentCalendar.map((calendar: CurrentCalendar) => calendar?.serviceId);
      // console.log("currentServiceIds:", currentServiceIds);
      dispatch(getCurrentCalendarBlockIds(currentServiceIds));
    }
  }, [agencyId, currentCalendar, dispatch, isAuthenticated]);

  //^ Get TA - MiamiDade and MiamiBeach downloadable content
  // Todo: move to the component?
  React.useEffect(() => {
    if (!isAuthenticated || !agencyId || !contentOnlyForTA || stopFetching) return;
    if (
      (configAgencyName === ("MiamiDade" as string) || configAgencyName === ("MiamiBeach" as string)) &&
      contentOnlyForTA
    ) {
      const setFetchInterval = () => {
        dispatch(getDownloadableTA_content(exportTypeAVL ?? exportTypeDefault));
        // console.log(6, "TA downloadable content was fetched", new Date().toLocaleTimeString("pl-PL"));
      };
      setFetchInterval();
      const fetchingInterval = setInterval(setFetchInterval, fetchInterval) as NodeJS.Timer;
      return () => {
        clearInterval(fetchingInterval);
        // console.log("Interval get TA downloadable content was cleared");
      };
    }
  }, [agencyId, dispatch, isAuthenticated, stopFetching]);

  //^ Get access to TA Scheduler V1.0
  React.useEffect(() => {
    if (!isAuthenticated || !agencyId || !contentOnlyForTA) return;
    if (contentOnlyForTA) {
      dispatch(getCredentials());
    }
  }, [agencyId, dispatch, isAuthenticated]);

  //* Get scheduleDetails fromTA Scheduler V1.0
  React.useEffect(() => {
    if (bearerToken) {
      const currentDate = dateToString2(new Date());
      dispatch(getScheduleDetails(bearerToken, currentDate));
    }
  }, [bearerToken, dispatch]);

  return (
    <React.Fragment>
      <Header
        setTableWidth={setTableWidth}
        setLoading={setLoading}
        loginWithRedirect={loginWithRedirect}
        user={user!}
        logout={logout}
        isLoading={isLoading}
        isAuthenticated={isAuthenticated}
      />

      {/* //* Loading */}
      {isLoading ? <TailSpinner height={"100vh"} /> : null}

      {/* //* ProtectedComponent */}
      <ProtectedComponent isAuthenticated={isAuthenticated} loginWithRedirect={loginWithRedirect}>
        <div className="container-fluid">
          {/* //* Top Bar */}
          <DivSwitch>
            <Button
              size="sm"
              variant="light"
              onClick={hideShowTableHandler}
              className={`buttons-legend ${hideSidebar ? "hide-button_hidden" : "hide-button"}`}
              disabled={hideMap ? true : false}
            >
              <img src={hideSidebar ? sidebarShowIcon : sidebarHideIcon} alt="Show/Hide sidebar" />
              <span style={{ textDecoration: hideMap ? "line-through" : "" }}>
                {hideSidebar ? t("Pokaż tabelę") : t("Ukryj tabelę")}
              </span>
            </Button>
            <DivRange>
              <span>{t("Proporcje")}</span>
              <RangeSlider
                disabled={hideMap || hideSidebar ? true : false}
                size="sm"
                variant="primary"
                value={tableWidth}
                onChange={(changeEvent) => {
                  try {
                    setLoading(true);
                    setTableWidth(Number(changeEvent.target.value));
                  } catch (error) {
                    console.log({ error });
                  }
                }}
                min={1}
                max={11}
                step={1}
                tooltip="auto"
                tooltipPlacement="top"
                tooltipLabel={(currentValue) =>
                  `${t("Proporcje")} ${((currentValue / 12) * 100).toFixed(0)}% /  ${(
                    ((12 - currentValue) / 12) *
                    100
                  ).toFixed(0)}%`
                }
              />
            </DivRange>
            <Form>
              <Form.Check
                inline
                type="switch"
                id="showFreeBuses"
                label={t("Pokaż nieprzypisane autobusy")}
                onChange={() => setShowFreeBuses(!showFreeBuses)}
                checked={showFreeBuses}
                disabled={
                  location?.pathname.includes("/tableV2")
                    ? false
                    : location?.pathname.includes("/views/bus") ||
                        location?.pathname.includes("/routesInfo") ||
                        location?.pathname.includes("/modifyTripStatus") ||
                        location?.pathname.includes("/averageSpeeds") ||
                        hideMap
                      ? true
                      : false
                }
              />
            </Form>
            {agencyId ? <ButtonsLegend agencyId={agencyId} /> : null}
          </DivSwitch>

          <div className="row">
            {/* //* Sidebar (left side) */}
            {!hideSidebar ? (
              <div className={hideMap ? "col-12" : `col-${tableWidth} p-0`}>
                {!agencyId || loadingTable ? (
                  <TailSpinner height={"100vh"} />
                ) : (
                  <React.Suspense fallback={null}>
                    {/* // Menu was modified and differs from directories tree! */}
                    <Routes>
                      <Route path="/" element={<TableBigComponent buses={buses!} agencyId={agencyId} />} />

                      {/* //^ On-Time Performance Table V2 */}
                      {process.env.NODE_ENV === "development" ? (
                        <Route path="/tableV2/onTimePerformance" element={<TableBigComponentV2Wrapper buses={buses!} />} />
                      ) : null}

                      {/* //* HeadwayPerformanceTable -> for TA - MiamiBeach only */}
                      <Route
                        path="/headwayPerformance"
                        element={
                          configAgencyName === ("MiamiBeach" as string) && contentOnlyForTA ? (
                            <HeadwayPerformanceTable buses={buses!} tableWidth={tableWidth} />
                          ) : (
                            <Navigate replace to="/" />
                          )
                        }
                      />
                      {/* //* Nested bus */}
                      <Route path="/views/bus">
                        <Route path="" element={<SelectedBusView />} />
                        <Route path=":busId" element={<SelectedBusView />} />
                      </Route>
                      <Route path="/views/routesInfo" element={<RoutesInfo />} />
                      <Route path="/views/modifyTripStatus" element={<ModifyTripStatus buses={buses!} />} />
                      <Route path="/views/accordion" element={<ActiveBlocksAccordion />} />

                      <Route path="/reports/singleAVL" element={<SingleReportAVL />} />
                      <Route path="/reports/deviation" element={<DeviationReport />} />
                      <Route path="/reports/last24hours" element={<Last24HoursReport />} />
                      <Route path="/reports/locationByDate" element={<BusLocationByDate />} />
                      {/* //* downloadableContentAVL -> for TA - MiamiDade and MiamiBeach */}
                      <Route
                        path="/downloads/downloadableContentTA"
                        element={
                          (configAgencyName === ("MiamiDade" as string) || configAgencyName === ("MiamiBeach" as string)) &&
                          contentOnlyForTA ? (
                            <DownloadAllVehiclesAVL />
                          ) : (
                            <Navigate replace to="/" />
                          )
                        }
                      />
                      <Route path="/downloads/downloadDailyStopsAdherence" element={<DownloadDailyStopsAdherence />} />
                      {/* //* Nested route */}
                      <Route path="/reports/singleTrip">
                        <Route path="" element={<SingleTripReport />} />
                        <Route path="date/:tripDate/trip/:tripId" element={<SingleTripReport />} />
                      </Route>

                      {/* //* Opens in a new window */}
                      <Route path="/window/routeSchedule" element={<ScheduleForRoute />} />
                      <Route path="/window/scheduleAdherenceAllRoutes" element={<ScheduleAdherenceAllRoutes />} />
                      <Route path="/window/allTrips" element={<AllTripsReport />} />
                      <Route path="/window/AllVehiclesAVLReport" element={<AllVehiclesAVLReport />} />
                      <Route path="/window/brigadeScheduleReport" element={<BrigadeScheduleReport />} />
                      <Route path="/window/stopPunctualityReport" element={<StopPunctualityReport />} />
                      <Route path="/window/synopticLiveLadderView" element={<SynopticLiveLadderView />} />
                      <Route path="/window/vehicleToBlockReport" element={<VehicleToBlockReport />} />

                      {/* //* downloadableStopsContent -> allStopsPunctualityReportDownLoad - PKS */}
                      <Route
                        path="/downloads/allStopsPunctualityReportDownLoad"
                        element={downloadableStopsData ? <AllStopsPunctualityReportDownLoad /> : <Navigate replace to="/" />}
                      />

                      {/* //* Opens in a new window: compareApisData -> for TA only - English only!!! */}
                      <Route
                        path="/window/compareApisData"
                        element={!contentOnlyForTA ? <Navigate replace to="/" /> : <CompareApisData />}
                      />

                      {/* //* Stats Charts: opens in a new window */}
                      <Route path="/window/predictionAccuracyInterval" element={<PredictionAccuracyInterval />} />
                      <Route path="/window/predictionAccuracyScatter" element={<PredictionAccuracyScatter />} />
                      <Route path="/window/predictionAccuracyRange" element={<PredictionAccuracyRange />} />

                      {/* //* Service Alerts Manager -> Nested route: takes up the entire window */}
                      <Route path="/window/alerts">
                        <Route path="" element={<ServiceAlertsManager agencyId={agencyId} />} />
                        <Route path="new" element={<AddNewAlert agencyId={agencyId} />} />
                        <Route path="details/:alertId" element={<AlertDetails agencyId={agencyId} />} />
                      </Route>

                      {/* //* AverageSpeeds - Downloads */}
                      <Route path="/downloads/averageSpeeds" element={<AverageSpeeds />} />

                      {/* //* DeleteVehiclesCache - the same window */}
                      <Route path="/window/deleteVehiclesCache" element={<DeleteVehiclesCache />} />

                      {/* //* Other components */}
                      <Route path="/not_found" element={<NotFound />} />
                      <Route path="/*" element={<NotFound />} />
                    </Routes>
                  </React.Suspense>
                )}
              </div>
            ) : null}

            {/* //* Map (right side) */}
            {!hideMap ? (
              <div className={hideSidebar ? "col-12" : `col-${12 - tableWidth} p-0`}>
                {/* //* appSettings (mapSettingsFromRedux) from Redux directly */}
                {agencyId &&
                initialMapRange &&
                mapSettingsFromRedux &&
                Object.keys(mapSettingsFromRedux)?.length >= 5 &&
                !loading ? (
                  <MapComponent
                    buses={buses!}
                    showLineNumbers={mapSettingsFromRedux?.showLineNumbers}
                    showDeviations={mapSettingsFromRedux?.showDeviations}
                    colorMap={mapSettingsFromRedux?.colorMap}
                    showAllRoutes={mapSettingsFromRedux?.showAllRoutes}
                    showTechnicalVehicles={mapSettingsFromRedux?.showTechnicalVehicles}
                    initialMapRange={initialMapRange}
                    selectedMapUrl={selectedMapUrl}
                    setSelectedMapUrl={setSelectedMapUrl}
                    agencyId={agencyId}
                  />
                ) : (
                  <TailSpinner height={"100vh"} />
                )}
              </div>
            ) : null}
          </div>
        </div>
      </ProtectedComponent>

      <Footer />

      <ToastContainer
        position="bottom-right"
        autoClose={3500}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick={true}
        pauseOnFocusLoss={true}
        draggable={false}
        pauseOnHover
        theme="colored"
      />

      {/* //* Custom Hook as React.VFC */}
      <UseShifts configAgencyName={configAgencyName as string} />
    </React.Fragment>
  );
}

export default App;
